import { useCallback, useEffect, useState } from "react";
import {
  closestCenter,
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";

import { Button, Drawer } from "@/components";
import { useResponsiveness } from "@/providers/context";
import { DRAWER_REORDER_PLAYLISTS, ITEM_TYPE_VIDEO } from "@/const.ts";
import { usePlaylists } from "@/hooks";
import { cn } from "@/utils";

import { usePlaylistDrawer, useReorderPlaylists } from "../hooks";
import { CreatePlaylistModal } from "./CreatePlaylistModal.tsx";
import { ReorderablePlaylist } from "./ReorderablePlaylist.tsx";

export const ReorderPlaylistsDrawer = () => {
  const [isModalOpen, setModalOpen] = useState(false);
  const { openDrawer, setOpenDrawer } = useResponsiveness();
  const { data: playlistData } = usePlaylists();
  const { getFirstItemInPlaylist } = usePlaylistDrawer({
    ItemId: 1,
    ItemType: ITEM_TYPE_VIDEO,
  });
  const reorderPlaylists = useReorderPlaylists();

  // Reset the state of the drawer if the data changes externally
  // e.g) If the user creates a new playlist
  useEffect(() => {
    setPlaylists(playlistData?.Playlists || []);
  }, [playlistData]);

  const [playlists, setPlaylists] = useState(playlistData?.Playlists ?? []);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (over && active.id !== over.id) {
        const oldIndex = playlists.findIndex((p) => p.PlaylistId === active.id);
        const newIndex = playlists.findIndex((p) => p.PlaylistId === over.id);
        setPlaylists((items) => {
          // Reorder based on the drag/drop
          const array = arrayMove(items, oldIndex, newIndex);
          // Set the position values
          return array.map((p, idx) => ({ ...p, Position: idx + 1 }));
        });
      }
    },
    [playlists],
  );
  const onSave = useCallback(() => {
    reorderPlaylists.mutate({ playlists });
    setOpenDrawer("");
  }, [playlists, reorderPlaylists, setOpenDrawer]);

  return (
    <Drawer
      title="Reorder"
      open={openDrawer === DRAWER_REORDER_PLAYLISTS}
      onOpenChange={(open) =>
        setOpenDrawer(open ? DRAWER_REORDER_PLAYLISTS : "")
      }
      dataTestId="reorder-playlist-drawer"
    >
      <div className="flex h-full flex-col justify-between pb-8">
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={playlists.map((p) => p.PlaylistId)}
            strategy={verticalListSortingStrategy}
          >
            <div className={cn("flex flex-col gap-6")}>
              {playlists.map((p) => {
                const firstItem = getFirstItemInPlaylist(p.PlaylistId);
                return (
                  <ReorderablePlaylist
                    key={p.PlaylistId}
                    playlist={p}
                    content={firstItem}
                  />
                );
              })}
            </div>
          </SortableContext>
        </DndContext>
        <div className="flex w-full flex-col gap-3">
          <CreatePlaylistModal open={isModalOpen} setOpen={setModalOpen} />
          <Button
            className="w-full"
            onClick={onSave}
            data-testid="playlist-save-button"
          >
            Confirm
          </Button>
        </div>
      </div>
    </Drawer>
  );
};
